import { useGameStore } from '../store/gameStore';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useTeamStore } from '../store/teamStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Trophy, Medal, Award, Home, RotateCcw } from 'lucide-react';

export const GameResults = () => {
  const { players, currentQuestionIndex, totalQuestions } = useGameStore();
  const { user } = useAuthStore();
  const { team1, team2 } = useTeamStore();
  const navigate = useNavigate();

  const sortedPlayers = [...players].sort((a, b) => b.score - a.score);
  const winner = sortedPlayers[0];
  const myPlayer = players.find((p) => p.userId === user?.id);
  const myRank = sortedPlayers.findIndex((p) => p.userId === user?.id) + 1;
  const completionRate = totalQuestions > 0 ? Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-accent-greenLight/10 to-primary flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl shadow-2xl border-2 border-accent-greenLight/30">
        <CardHeader className="bg-gradient-to-r from-secondary to-secondary-dark text-white text-center">
          <div className="flex justify-center mb-4">
            <Trophy className="w-16 h-16" />
          </div>
          <CardTitle className="text-4xl md:text-5xl font-bold" style={{ fontFamily: "'Almarai', 'Cairo', sans-serif" }}>
            النتائج النهائية
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8 p-8">
          {/* Winner Section */}
          <div className="text-center bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-8 border-4 border-yellow-400">
            <div className="flex justify-center mb-4">
              <Trophy className="w-20 h-20 text-yellow-500" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-2" style={{ fontFamily: "'Almarai', 'Cairo', sans-serif" }}>
              🏆 الفائز: {winner.username}
            </h2>
            <p className="text-5xl md:text-6xl font-bold text-secondary mt-4">{winner.score} نقطة</p>
            {team1 && winner.username === team1.name && (
              <p className="text-lg text-red-600 mt-2 font-semibold">({team1.name})</p>
            )}
            {team2 && winner.username === team2.name && (
              <p className="text-lg text-green-600 mt-2 font-semibold">({team2.name})</p>
            )}
          </div>

          {/* Game Stats */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-primary/10 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">الأسئلة المكتملة</p>
              <p className="text-2xl font-bold text-primary">{currentQuestionIndex + 1} / {totalQuestions}</p>
            </div>
            <div className="bg-primary/10 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">نسبة الإكمال</p>
              <p className="text-2xl font-bold text-primary">{completionRate}%</p>
            </div>
            <div className="bg-primary/10 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">عدد اللاعبين</p>
              <p className="text-2xl font-bold text-primary">{players.length}</p>
            </div>
          </div>

          {/* Rankings */}
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-primary mb-4 flex items-center gap-2" style={{ fontFamily: "'Almarai', 'Cairo', sans-serif" }}>
              <Medal className="w-6 h-6 text-secondary" />
              الترتيب النهائي
            </h3>
            {sortedPlayers.map((player, index) => {
              const isMe = player.userId === user?.id;
              const isWinner = index === 0;
              const isSecond = index === 1;
              const isThird = index === 2;
              
              return (
                <div
                  key={player.userId}
                  className={`flex items-center justify-between p-4 rounded-lg transition-all ${
                    isWinner
                      ? 'bg-gradient-to-r from-yellow-100 to-yellow-50 border-4 border-yellow-400 shadow-lg'
                      : isSecond
                      ? 'bg-gradient-to-r from-gray-100 to-gray-50 border-2 border-gray-300'
                      : isThird
                      ? 'bg-gradient-to-r from-orange-100 to-orange-50 border-2 border-orange-300'
                      : isMe
                      ? 'bg-primary/10 border-2 border-primary'
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {isWinner && <Trophy className="w-8 h-8 text-yellow-500" />}
                    {isSecond && <Medal className="w-8 h-8 text-gray-400" />}
                    {isThird && <Award className="w-8 h-8 text-orange-500" />}
                    {!isWinner && !isSecond && !isThird && (
                      <span className="text-xl font-bold text-gray-500 w-8 text-center">#{index + 1}</span>
                    )}
                    <div>
                      <span className={`text-lg font-semibold ${isMe ? 'text-primary' : 'text-gray-800'}`}>
                        {player.username}
                      </span>
                      {isMe && <span className="text-sm text-primary mr-2">(أنت)</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-secondary">{player.score}</span>
                    <span className="text-sm text-gray-600 mr-2">نقطة</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* My Rank Card */}
          {myPlayer && (
            <div className="bg-gradient-to-r from-secondary/20 to-secondary-dark/20 rounded-xl p-6 border-2 border-secondary text-center">
              <p className="text-sm text-gray-600 mb-2">ترتيبك في هذه اللعبة</p>
              <p className="text-4xl font-bold text-secondary mb-2">#{myRank}</p>
              <p className="text-2xl font-semibold text-primary">{myPlayer.score} نقطة</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button 
              onClick={() => navigate('/player/lobby')}
              className="bg-secondary hover:bg-secondary-dark text-white px-8 py-6 text-lg font-bold flex items-center gap-2"
              size="lg"
            >
              <RotateCcw className="w-5 h-5" />
              العب مرة أخرى
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/player')}
              className="border-2 border-primary text-primary hover:bg-primary/10 px-8 py-6 text-lg font-bold flex items-center gap-2"
              size="lg"
            >
              <Home className="w-5 h-5" />
              الرئيسية
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
